import { Injectable, signal } from '@angular/core';

/**
 * WI 32033: suffix for the device-storage key that keeps the exemptions across
 * a cold app restart. Appended to the user-scoped job-store key
 * (`<scope>|GET-<job list url>`), so the exemptions are scoped exactly like the
 * job list they qualify, and the #33948 sign-out / user-switch purge deletes
 * them with that user's other cached data (no retainer claims the key).
 */
export const FOREMAN_STALE_HIDE_EXEMPTIONS_KEY_SUFFIX =
  '|stale-hide-exemptions';

export function foremanStaleHideExemptionsKey(jobsCacheKey: string): string {
  return `${jobsCacheKey}${FOREMAN_STALE_HIDE_EXEMPTIONS_KEY_SUFFIX}`;
}

/**
 * Device storage for the exemptions, plugged in by the app (the same pattern
 * as `ForemanJobService.getPersistedJobs`), so this lib stays platform-free.
 * `load` returns null when nothing may be read (no signed-in user owns the
 * cache yet); `save` must be a no-op in that state.
 */
export interface ForemanJobVisibilityStore {
  load(): readonly string[] | null;
  save(foremanJobIDs: readonly string[]): void;
}

/**
 * The device-storage `ForemanJobVisibilityStore` (NativeScript ApplicationSettings
 * fits `kv`). Like the job store, it neither reads nor writes until a user owns
 * the cache (`isScoped`), and the key is resolved on every call because the
 * scope can change after startup. An unreadable value reads as no exemptions:
 * the worst case is the pre-32033 behaviour, a stale job hidden until re-added.
 */
export function createForemanJobVisibilityStore(
  kv: {
    getString(key: string): string | undefined;
    setString(key: string, value: string): void;
  },
  jobsCacheKey: () => string,
  isScoped: () => boolean
): ForemanJobVisibilityStore {
  return {
    load: () => {
      if (!isScoped()) {
        return null;
      }
      try {
        const raw = kv.getString(foremanStaleHideExemptionsKey(jobsCacheKey()));
        const ids: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(ids) ? (ids as string[]) : [];
      } catch {
        return [];
      }
    },
    save: (foremanJobIDs) => {
      if (!isScoped()) {
        return;
      }
      kv.setString(
        foremanStaleHideExemptionsKey(jobsCacheKey()),
        JSON.stringify(foremanJobIDs)
      );
    },
  };
}

/**
 * Root-scoped holder for the Foreman job-list visibility exemptions (WI 31896).
 *
 * The US-30867 stale filter hides a dispatched job with no keyed time and no
 * live dispatch in the trailing window. Picking that job out of the job
 * typeahead is the documented way to bring it back, so the id is recorded here
 * and the shared predicate (`isForemanJobHidden`) skips it.
 *
 * WHY THIS IS ITS OWN ROOT-SCOPED SERVICE, and not state on the page component
 * or on `ForemanJobService`:
 *
 *  - NOT the component. The mobile job-selection page is destroyed and
 *    recreated on every navigation (`page.on('navigatedTo')` -> `_initPage` ->
 *    `_showList`), so component state cannot survive the round trip INTO a job
 *    and back out. That round trip is exactly what made a re-added job vanish
 *    again, so component-scoped state would not fix the reported defect.
 *  - NOT `ForemanJobService`. That service is already 1159 lines and the
 *    file-size gate blocks growing a file that is over 500. Splitting the
 *    concern out is the remedy the gate asks for, and it is the better shape
 *    anyway: this is one narrow piece of view state with no HTTP, no cache and
 *    no dependencies, whereas that service owns the job graph and its
 *    persistence.
 *
 * `providedIn: 'root'` keeps the exemption across navigation. WI 32033: the
 * process does not survive a cold restart, so every change is also written to
 * `store`, and the job-selection page calls `rehydrate()` before it filters.
 */
@Injectable({ providedIn: 'root' })
export class ForemanJobVisibilityState {
  /**
   * `foremanJobID`s the user deliberately brought back into the list. Exposed
   * as a signal so a template or computed can react, and as a ReadonlySet so a
   * caller cannot mutate it in place behind the signal.
   */
  readonly manuallyAddedJobIds = signal<ReadonlySet<string>>(new Set<string>());

  /** Set by the app at startup; null leaves the exemptions in memory only. */
  store: ForemanJobVisibilityStore | null = null;

  /**
   * Replace the in-memory set with what the store holds for the signed-in
   * user (empty when nothing may be read). Without a store this is a no-op,
   * so in-memory exemptions are never dropped for lack of persistence.
   */
  rehydrate(): void {
    if (!this.store) {
      return;
    }
    const stored = this.store.load() ?? [];
    const next = new Set(
      stored.filter((id): id is string => typeof id === 'string' && !!id)
    );
    const current = this.manuallyAddedJobIds();
    if (
      next.size === current.size &&
      [...next].every((id) => current.has(id))
    ) {
      return;
    }
    this.manuallyAddedJobIds.set(next);
  }

  /**
   * Drop the in-memory set when the signed-in user changes, WITHOUT touching
   * storage: the outgoing user's copy stays under their key until the purge
   * deletes it, and the next `rehydrate()` reads the incoming user's.
   */
  releaseScope(): void {
    if (this.manuallyAddedJobIds().size > 0) {
      this.manuallyAddedJobIds.set(new Set<string>());
    }
  }

  /**
   * Exempt one job from the stale-hide filter. Idempotent: re-adding an
   * already-exempt job does not churn the signal, so it cannot cause a
   * redundant list rebuild.
   */
  exemptJobFromStaleHide(foremanJobID: string | null | undefined): void {
    if (!foremanJobID) {
      return;
    }
    const current = this.manuallyAddedJobIds();
    if (current.has(foremanJobID)) {
      return;
    }
    const next = new Set(current);
    next.add(foremanJobID);
    this.manuallyAddedJobIds.set(next);
    this.store?.save([...next]);
  }

  /** Drop every exemption, in memory and in storage. */
  clearStaleHideExemptions(): void {
    if (this.manuallyAddedJobIds().size === 0) {
      return;
    }
    this.manuallyAddedJobIds.set(new Set<string>());
    this.store?.save([]);
  }
}
